/* Species Registry — the single source of truth for how every real-world
 * species is presented in the 3D arena. Battle Cards reference their
 * species by `species_common_name` and this registry maps that name to:
 *
 *   - which renderer to use (procedural placeholder now, GLB later),
 *   - real-world size (for camera framing + world-scale correctness),
 *   - animation clip names (idle / attack / damage / victory / defeat / dodge),
 *   - audio track keys (ambient hint, attack, damage, victory),
 *   - particle presets (footstep, impact),
 *   - material colour hints for the procedural fallback,
 *   - camera framing hints (target offset, extra padding).
 *
 * ARCHITECTURAL CONTRACT (per user directive of iter31):
 *   "Do not tightly couple creatures to the current placeholder models."
 *
 * When a professional GLB asset arrives for a species, upgrade its `model`
 * block only. Every other part of the pipeline reads from this registry —
 * NOTHING else needs to change.
 */

/**
 * @typedef {'mammal_quadruped' | 'mammal_bear' | 'bird' | 'raptor' | 'reptile' | 'fish' | 'insect' | 'plant'} Archetype
 * @typedef {'procedural' | 'glb'} RendererType
 */

/** @typedef {Object} SpeciesSpec
 *  @property {string} common_name              Canonical common name key.
 *  @property {string} scientific_name          Latin binomial.
 *  @property {string} category                 Coarse category (Mammals / Birds / …).
 *  @property {Archetype} archetype             Renderer archetype.
 *  @property {{type: RendererType, url?: string, name?: string}} model
 *  @property {{height_m: number, length_m: number}} size    Real-world size.
 *  @property {{idle: string, attack: string, damage: string, dodge: string, victory: string, defeat: string}} animations
 *  @property {{attack?: string, damage?: string, victory?: string, ambient?: string}} sfx
 *  @property {{footstep?: string, impact?: string, aura?: string}} particles
 *  @property {{primary: string, secondary?: string, accent?: string}} materials
 *  @property {{target_offset_y?: number, framing_padding?: number}} [camera]
 */

const DEFAULT_ANIMATIONS = {
  idle: 'idle',
  attack: 'attack',
  damage: 'damage',
  dodge: 'dodge',
  victory: 'victory',
  defeat: 'defeat',
};

const DEFAULT_SFX = {
  attack: 'generic_attack',
  damage: 'generic_damage',
  victory: 'generic_victory',
  ambient: 'generic_ambient',
};

const DEFAULT_PARTICLES = { footstep: 'dust', impact: 'impact_dust' };

/** @type {Record<string, SpeciesSpec>} */
export const SPECIES = {
  'Gray Wolf': {
    common_name: 'Gray Wolf', scientific_name: 'Canis lupus', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'wolf' /* future: { type: 'glb', url: '/models/wolf.glb' } */ },
    size: { height_m: 0.85, length_m: 1.6 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, attack: 'wolf_growl', victory: 'wolf_howl', ambient: 'forest_wind' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#7c8489', secondary: '#4a5157', accent: '#e9d8b6' },
    camera:     { target_offset_y: 0.6, framing_padding: 1.15 },
  },
  'American Black Bear': {
    common_name: 'American Black Bear', scientific_name: 'Ursus americanus', category: 'Mammals',
    archetype: 'mammal_bear',
    model: { type: 'procedural', name: 'bear' },
    size: { height_m: 1.1, length_m: 1.8 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, attack: 'bear_roar', damage: 'bear_grunt', ambient: 'forest_ambient' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#1a1614', secondary: '#0d0b0a', accent: '#3a2f2a' },
    camera:     { target_offset_y: 0.8, framing_padding: 1.25 },
  },
  'Polar Bear': {
    common_name: 'Polar Bear', scientific_name: 'Ursus maritimus', category: 'Mammals',
    archetype: 'mammal_bear',
    model: { type: 'procedural', name: 'bear_polar' },
    size: { height_m: 1.5, length_m: 2.6 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'arctic_wind' },
    particles:  { ...DEFAULT_PARTICLES, footstep: 'snow' },
    materials:  { primary: '#f0efe8', secondary: '#d9d7cf', accent: '#e2ddce' },
    camera:     { target_offset_y: 1.0, framing_padding: 1.3 },
  },
  'Bald Eagle': {
    common_name: 'Bald Eagle', scientific_name: 'Haliaeetus leucocephalus', category: 'Birds',
    archetype: 'raptor',
    model: { type: 'procedural', name: 'eagle' },
    size: { height_m: 0.9, length_m: 2.2 /* wingspan */ },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, attack: 'eagle_screech', ambient: 'coastal_wind' },
    particles:  { ...DEFAULT_PARTICLES, footstep: 'feather' },
    materials:  { primary: '#3a2a1c', secondary: '#f5f0e8', accent: '#e8b840' },
    camera:     { target_offset_y: 1.4, framing_padding: 1.2 },
  },
  'Great Blue Heron': {
    common_name: 'Great Blue Heron', scientific_name: 'Ardea herodias', category: 'Birds',
    archetype: 'bird',
    model: { type: 'procedural', name: 'heron' },
    size: { height_m: 1.2, length_m: 1.8 /* wingspan */ },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'marsh_ambient' },
    particles:  { ...DEFAULT_PARTICLES, footstep: 'water_splash' },
    materials:  { primary: '#7c8895', secondary: '#4a5865', accent: '#f2c94c' },
    camera:     { target_offset_y: 0.8, framing_padding: 1.1 },
  },
  'American Alligator': {
    common_name: 'American Alligator', scientific_name: 'Alligator mississippiensis', category: 'Reptiles',
    archetype: 'reptile',
    model: { type: 'procedural', name: 'alligator' },
    size: { height_m: 0.4, length_m: 3.4 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, attack: 'gator_hiss', ambient: 'swamp_ambient' },
    particles:  { ...DEFAULT_PARTICLES, footstep: 'water_splash' },
    materials:  { primary: '#2f3a2c', secondary: '#1a1f18', accent: '#8a7355' },
    camera:     { target_offset_y: 0.3, framing_padding: 1.35 },
  },
  'Mountain Lion': {
    common_name: 'Mountain Lion', scientific_name: 'Puma concolor', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'cougar' },
    size: { height_m: 0.75, length_m: 1.5 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, attack: 'cougar_snarl', ambient: 'mountain_wind' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#b58a55', secondary: '#7a5a34', accent: '#e8d5b0' },
    camera:     { target_offset_y: 0.55, framing_padding: 1.15 },
  },
  'Rocky Mountain Goat': {
    common_name: 'Rocky Mountain Goat', scientific_name: 'Oreamnos americanus', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'goat' },
    size: { height_m: 0.9, length_m: 1.5 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'mountain_wind' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#e6ded1', secondary: '#a99f8b', accent: '#1a1614' },
    camera:     { target_offset_y: 0.6, framing_padding: 1.15 },
  },
  'California Sea Lion': {
    common_name: 'California Sea Lion', scientific_name: 'Zalophus californianus', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'sealion' },
    size: { height_m: 0.9, length_m: 2.0 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'coastal_waves' },
    particles:  { ...DEFAULT_PARTICLES, footstep: 'water_splash' },
    materials:  { primary: '#3a2f28', secondary: '#1e1815', accent: '#856b52' },
    camera:     { target_offset_y: 0.55, framing_padding: 1.2 },
  },
  'Red-tailed Hawk': {
    common_name: 'Red-tailed Hawk', scientific_name: 'Buteo jamaicensis', category: 'Birds',
    archetype: 'raptor',
    model: { type: 'procedural', name: 'hawk' },
    size: { height_m: 0.55, length_m: 1.2 /* wingspan */ },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, attack: 'hawk_scream', ambient: 'open_air' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#8b5a35', secondary: '#3a2418', accent: '#d4a06b' },
    camera:     { target_offset_y: 1.0, framing_padding: 1.1 },
  },
  'Red Fox': {
    common_name: 'Red Fox', scientific_name: 'Vulpes vulpes', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'fox' },
    size: { height_m: 0.4, length_m: 0.9 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'forest_ambient' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#c25a2a', secondary: '#3a1e12', accent: '#f5efe4' },
    camera:     { target_offset_y: 0.3, framing_padding: 1.05 },
  },
  'Moose': {
    common_name: 'Moose', scientific_name: 'Alces alces', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'moose' },
    size: { height_m: 2.0, length_m: 2.7 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'boreal_forest' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#3a2418', secondary: '#1e130a', accent: '#8b6a4a' },
    camera:     { target_offset_y: 1.3, framing_padding: 1.35 },
  },
  'White-tailed Deer': {
    common_name: 'White-tailed Deer', scientific_name: 'Odocoileus virginianus', category: 'Mammals',
    archetype: 'mammal_quadruped',
    model: { type: 'procedural', name: 'deer' },
    size: { height_m: 1.0, length_m: 1.8 },
    animations: DEFAULT_ANIMATIONS,
    sfx:        { ...DEFAULT_SFX, ambient: 'forest_ambient' },
    particles:  DEFAULT_PARTICLES,
    materials:  { primary: '#a06d3a', secondary: '#f5efe4', accent: '#3a2418' },
    camera:     { target_offset_y: 0.7, framing_padding: 1.15 },
  },
};

/** Archetype fallbacks (used when we don't have a bespoke species spec yet). */
const ARCHETYPE_FALLBACK = {
  mammal_quadruped: {
    size: { height_m: 0.9, length_m: 1.4 }, materials: { primary: '#8fa87b' },
    camera: { target_offset_y: 0.6, framing_padding: 1.15 },
  },
  mammal_bear: {
    size: { height_m: 1.1, length_m: 1.8 }, materials: { primary: '#3a2f28' },
    camera: { target_offset_y: 0.8, framing_padding: 1.25 },
  },
  bird: {
    size: { height_m: 0.6, length_m: 0.9 }, materials: { primary: '#9fb08c' },
    camera: { target_offset_y: 0.7, framing_padding: 1.1 },
  },
  raptor: {
    size: { height_m: 0.7, length_m: 1.4 }, materials: { primary: '#7c5a35' },
    camera: { target_offset_y: 1.1, framing_padding: 1.2 },
  },
  reptile: {
    size: { height_m: 0.35, length_m: 1.6 }, materials: { primary: '#4a5a3a' },
    camera: { target_offset_y: 0.25, framing_padding: 1.3 },
  },
  fish: {
    size: { height_m: 0.4, length_m: 0.7 }, materials: { primary: '#4d7ea8' },
    camera: { target_offset_y: 0.5, framing_padding: 1.1 },
  },
  insect: {
    size: { height_m: 0.15, length_m: 0.2 }, materials: { primary: '#7a5a34' },
    camera: { target_offset_y: 0.15, framing_padding: 0.85 },
  },
  plant: {
    size: { height_m: 1.3, length_m: 1.0 }, materials: { primary: '#4a7a3a' },
    camera: { target_offset_y: 0.9, framing_padding: 1.1 },
  },
};

const CATEGORY_TO_ARCHETYPE = {
  Birds: 'bird', Mammals: 'mammal_quadruped', Rodents: 'mammal_quadruped',
  Reptiles: 'reptile', Amphibians: 'reptile', Fish: 'fish',
  Insects: 'insect', Plants: 'plant', Trees: 'plant', Mushrooms: 'plant',
};

/** Resolve a species spec. Falls back to archetype defaults when the
 *  species isn't in the registry yet — no code branches, one contract.
 */
export function resolveSpecies(snap = {}) {
  const key = snap.common_name || snap.species_common_name || snap.name;
  if (key && SPECIES[key]) return SPECIES[key];

  const category = snap.species_category || snap.category || 'Mammals';
  const archetype = CATEGORY_TO_ARCHETYPE[category] || 'mammal_quadruped';
  const fb = ARCHETYPE_FALLBACK[archetype];
  return {
    common_name: key || 'Unknown',
    scientific_name: snap.species_scientific_name || snap.scientific_name || 'Unknown',
    category,
    archetype,
    model: { type: 'procedural', name: `${archetype}_default` },
    size: fb.size,
    animations: DEFAULT_ANIMATIONS,
    sfx: DEFAULT_SFX,
    particles: DEFAULT_PARTICLES,
    materials: fb.materials,
    camera: fb.camera,
  };
}

/** Compute the world scale for a creature so that its rendered height
 *  matches its real-world height (with a global multiplier).
 *
 *  Placeholder rigs are drawn at a nominal 1.0 unit height, so scale =
 *  size.height_m / nominal. When a GLB arrives, its authoring scale is
 *  already correct — this helper still applies the framing multiplier.
 */
export function worldScaleFor(spec, { nominalHeight = 1.0, worldScale = 1.0 } = {}) {
  return (spec.size.height_m / nominalHeight) * worldScale;
}


/* computeCombatPositions — single source of truth for where the two
 * combatants stand. Both CinematicScene and CinematicCamera consume this
 * so occlusion raycasts, camera framing, and creature placement stay in
 * sync no matter which species compete. */
export function computeCombatPositions(chSnap, opSnap) {
  const specCh = resolveSpecies(chSnap);
  const specOp = resolveSpecies(opSnap);
  const sep = Math.max(4, (specCh.size.length_m + specOp.size.length_m) * 1.2);
  return { chPos: [-sep / 2, 0, 0], opPos: [sep / 2, 0, 0] };
}
